import type { ChatMessage, ChatRoom } from './api';

/**
 * A deliberately small, origin-local cache for private chat UI.
 *
 * The cache never contains credentials, signed URLs, full attachment files,
 * or unsent drafts. Small image previews are stored separately with strict
 * count, per-item, total-byte, and time limits. Every entry is scoped to the
 * authenticated Nextcloud user so a shared device cannot expose one person's
 * family chat to another.
 */
const DATABASE_NAME = 'family-photos-private-chat-v1';
const STORE_NAME = 'entries';
const MAX_ROOMS = 50;
const MAX_MESSAGES_PER_ROOM = 100;
const MAX_ATTACHMENT_PREVIEWS = 24;
const MAX_ATTACHMENT_PREVIEW_BYTES = 1024 * 1024;
const MAX_ATTACHMENT_PREVIEW_TOTAL_BYTES = 8 * 1024 * 1024;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type CacheEntry = InboxEntry | TimelineEntry | AttachmentPreviewEntry;

interface BaseEntry {
  key: string;
  userId: string;
  cachedAt: number;
  expiresAt: number;
}

interface InboxEntry extends BaseEntry {
  kind: 'inbox';
  rooms: ChatRoom[];
}

interface TimelineEntry extends BaseEntry {
  kind: 'timeline';
  roomToken: string;
  messages: ChatMessage[];
}

interface AttachmentPreviewEntry extends BaseEntry {
  kind: 'attachment-preview';
  roomToken: string;
  messageId: number;
  fileId: string;
  blob: Blob;
  byteSize: number;
}

export interface CachedChatData<T> {
  data: T;
  cachedAt: number;
}

export async function readCachedInbox(
  userId: string,
): Promise<CachedChatData<ChatRoom[]> | null> {
  const entry = await readEntry(inboxKey(userId));
  if (
    !entry
    || entry.kind !== 'inbox'
    || entry.userId !== userId
    || !Number.isFinite(entry.cachedAt)
  ) return null;
  try {
    return { data: sanitizeRooms(entry.rooms), cachedAt: entry.cachedAt };
  } catch {
    return null;
  }
}

export async function writeCachedInbox(userId: string, rooms: ChatRoom[]): Promise<void> {
  if (!userId) return;
  const now = Date.now();
  await writeEntry({
    key: inboxKey(userId),
    kind: 'inbox',
    userId,
    cachedAt: now,
    expiresAt: now + CACHE_TTL_MS,
    rooms: sanitizeRooms(rooms),
  });
}

export async function readCachedTimeline(
  userId: string,
  roomToken: string,
): Promise<CachedChatData<ChatMessage[]> | null> {
  const entry = await readEntry(timelineKey(userId, roomToken));
  if (
    !entry
    || entry.kind !== 'timeline'
    || entry.userId !== userId
    || entry.roomToken !== roomToken
    || !Number.isFinite(entry.cachedAt)
  ) return null;
  try {
    return { data: sanitizeMessages(entry.messages), cachedAt: entry.cachedAt };
  } catch {
    return null;
  }
}

export async function writeCachedTimeline(
  userId: string,
  roomToken: string,
  messages: ChatMessage[],
): Promise<void> {
  if (!userId || !roomToken) return;
  const now = Date.now();
  await writeEntry({
    key: timelineKey(userId, roomToken),
    kind: 'timeline',
    userId,
    roomToken,
    cachedAt: now,
    expiresAt: now + CACHE_TTL_MS,
    messages: sanitizeMessages(messages),
  });
}

export async function readCachedAttachmentPreview(
  userId: string,
  roomToken: string,
  messageId: number,
  fileId: string,
): Promise<Blob | null> {
  const entry = await readEntry(attachmentPreviewKey(userId, roomToken, messageId, fileId));
  if (
    !entry
    || entry.kind !== 'attachment-preview'
    || entry.userId !== userId
    || entry.roomToken !== roomToken
    || entry.messageId !== messageId
    || entry.fileId !== fileId
    || !(entry.blob instanceof Blob)
    || !entry.blob.type.startsWith('image/')
    || entry.blob.size <= 0
    || entry.blob.size > MAX_ATTACHMENT_PREVIEW_BYTES
  ) return null;
  return entry.blob;
}

export async function writeCachedAttachmentPreview(
  userId: string,
  roomToken: string,
  messageId: number,
  fileId: string,
  blob: Blob,
): Promise<void> {
  if (
    !userId
    || !roomToken
    || !Number.isSafeInteger(messageId)
    || messageId <= 0
    || !fileId
    || !blob.type.startsWith('image/')
    || blob.size <= 0
    || blob.size > MAX_ATTACHMENT_PREVIEW_BYTES
  ) return;

  const now = Date.now();
  await writeEntry({
    key: attachmentPreviewKey(userId, roomToken, messageId, fileId),
    kind: 'attachment-preview',
    userId,
    roomToken,
    messageId,
    fileId,
    blob,
    byteSize: blob.size,
    cachedAt: now,
    expiresAt: now + CACHE_TTL_MS,
  });
  await pruneAttachmentPreviews(userId);
}

/** Remove every private chat record. Called before auth state is discarded. */
export async function clearChatCache(): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, 'readwrite', (store) => store.clear());
}

/**
 * Account switching without a formal logout must not leave another person's
 * messages behind. Keep only records that belong to the newly authenticated
 * account.
 */
export async function purgeChatCacheExcept(userId: string): Promise<void> {
  const database = await openDatabase();
  if (!database) return;

  await runTransaction(database, 'readwrite', (store) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      const value = cursor.value as Partial<CacheEntry>;
      if (value.userId !== userId) cursor.delete();
      cursor.continue();
    };
  });
}

function inboxKey(userId: string): string {
  return `inbox:${encodeURIComponent(userId)}`;
}

function timelineKey(userId: string, roomToken: string): string {
  return `timeline:${encodeURIComponent(userId)}:${encodeURIComponent(roomToken)}`;
}

function attachmentPreviewKey(
  userId: string,
  roomToken: string,
  messageId: number,
  fileId: string,
): string {
  return [
    'attachment-preview',
    encodeURIComponent(userId),
    encodeURIComponent(roomToken),
    String(messageId),
    encodeURIComponent(fileId),
  ].join(':');
}

async function pruneAttachmentPreviews(userId: string): Promise<void> {
  const database = await openDatabase();
  if (!database) return;

  await runTransaction(database, 'readwrite', (store) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const now = Date.now();
      const previews = (request.result as CacheEntry[])
        .filter((entry): entry is AttachmentPreviewEntry => (
          entry.kind === 'attachment-preview' && entry.userId === userId
        ))
        .sort((left, right) => right.cachedAt - left.cachedAt);

      let kept = 0;
      let totalBytes = 0;
      for (const preview of previews) {
        const byteSize = Number.isFinite(preview.byteSize) ? preview.byteSize : preview.blob?.size;
        const mustDelete = (
          preview.expiresAt <= now
          || !Number.isFinite(byteSize)
          || byteSize <= 0
          || byteSize > MAX_ATTACHMENT_PREVIEW_BYTES
          || kept >= MAX_ATTACHMENT_PREVIEWS
          || totalBytes + byteSize > MAX_ATTACHMENT_PREVIEW_TOTAL_BYTES
        );
        if (mustDelete) {
          store.delete(preview.key);
        } else {
          kept += 1;
          totalBytes += byteSize;
        }
      }
    };
  });
}

async function readEntry(key: string): Promise<CacheEntry | null> {
  const database = await openDatabase();
  if (!database) return null;

  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => {
      const entry = request.result as CacheEntry | undefined;
      if (!entry || typeof entry.expiresAt !== 'number' || entry.expiresAt <= Date.now()) {
        if (entry) store.delete(key);
        resolve(null);
        return;
      }
      resolve(entry);
    };
    request.onerror = () => resolve(null);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => {
      database.close();
      resolve(null);
    };
    transaction.onabort = () => {
      database.close();
      resolve(null);
    };
  });
}

async function writeEntry(entry: CacheEntry): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  await runTransaction(database, 'readwrite', (store) => store.put(entry));
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);

  return new Promise((resolve) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

function runTransaction(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest | void,
): Promise<void> {
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, mode);
    try {
      operation(transaction.objectStore(STORE_NAME));
    } catch {
      database.close();
      resolve();
      return;
    }
    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => {
      database.close();
      resolve();
    };
    transaction.onabort = () => {
      database.close();
      resolve();
    };
  });
}

function sanitizeRooms(rooms: ChatRoom[]): ChatRoom[] {
  return rooms
    .filter((room) => (
      typeof room?.token === 'string'
      && room.token.length > 0
      && typeof room.name === 'string'
      && typeof room.type === 'number'
      && typeof room.unread === 'number'
      && typeof room.lastActivity === 'number'
      && typeof room.preview === 'string'
    ))
    .slice(0, MAX_ROOMS)
    .map((room) => ({
      token: room.token,
      name: room.name,
      type: room.type,
      unread: Math.max(0, room.unread),
      unreadMention: Boolean(room.unreadMention),
      lastActivity: room.lastActivity,
      preview: room.preview,
    }));
}

function sanitizeMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages
    .filter((message) => (
      !message.deliveryState
      && Number.isFinite(message.id)
      && message.id > 0
      && typeof message.actorId === 'string'
      && typeof message.actorDisplayName === 'string'
      && typeof message.message === 'string'
      && Number.isFinite(message.timestamp)
    ))
    .slice(-MAX_MESSAGES_PER_ROOM)
    .map((message) => ({
      id: message.id,
      ...(message.referenceId ? { referenceId: message.referenceId } : {}),
      actorId: message.actorId,
      actorDisplayName: message.actorDisplayName,
      message: message.message,
      timestamp: message.timestamp,
      isOwn: Boolean(message.isOwn),
      isReplyable: Boolean(message.isReplyable),
      reactions: Array.isArray(message.reactions) ? message.reactions.map((reaction) => ({
        reaction: reaction.reaction,
        count: reaction.count,
        reacted: Boolean(reaction.reacted),
      })) : [],
      parent: message.parent ? {
        id: message.parent.id,
        actorId: message.parent.actorId,
        actorDisplayName: message.parent.actorDisplayName,
        message: message.parent.message,
        deleted: Boolean(message.parent.deleted),
        isOwn: Boolean(message.parent.isOwn),
      } : null,
      // Metadata is enough to render the card. Any separately cached image is
      // a resized preview; protected originals are never persisted here.
      attachment: message.attachment ? {
        fileId: message.attachment.fileId,
        name: message.attachment.name,
        mimeType: message.attachment.mimeType,
        size: message.attachment.size,
        previewAvailable: Boolean(message.attachment.previewAvailable),
      } : null,
    }));
}
